import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, X } from 'lucide-react';
import api from '../../services/api';
import '../../css/guided-entry.css';
import Illustration from '../ui/Illustration';
import illuGuide from '../../assets/illustrations/apprentissage-guide.svg?raw';

// Même recommandation que le parcours guidé (learning-plan.php → GuidedLearning::recommendation) :
// une seule source de conseil sur l'écran « Ta prochaine étape ».
// `extra` (raccourcis de reprise après absence) s'affiche entre la recommandation et les modes.
// Taille de la séance de révisions lancée par le bouton (GuidedLearning.launchSm2 : les plus urgentes d'abord).
const SEANCE_SM2 = 20;
// Séance courte proposée à côté, quand le retard dépasse cette taille.
const EXPRESS_SM2 = 10;
// « Changer » de concours : la carte est recréée (clé = parcours), la demande de retour au choix vit donc ici.
let retourAuChoixDemande = false;
const TEXTS = {
    // Le bouton démarre la séance tout de suite : le libellé dit laquelle.
    bilan:   { title: 'Fais ton premier bilan', sub: '16 questions maximum pour repérer tes points d’appui et tes priorités.', cta: 'Démarrer le bilan' },
    rebilan: { title: 'Refais le point', sub: 'Un nouveau bilan pour mesurer tes progrès.', cta: 'Démarrer le bilan' },
    sm2:     { title: n => `${n} révision${n > 1 ? 's' : ''} à faire aujourd’hui`, sub: 'Les questions qui reviennent au bon moment pour être retenues.', cta: 'Démarrer les révisions' },
    weak:    { title: r => `À travailler : ${r.label || 'ton sujet prioritaire'}`, sub: 'Le sujet le plus prioritaire selon tes résultats.', cta: 'Démarrer la séance' },
    explore: { title: 'Complète ton bilan', sub: 'Des sujets restent à découvrir : 16 questions inédites en priorité.', cta: 'Démarrer la suite du bilan' },
};

/**
 * Tableau des domaines du bilan (learning-plan « bilan_domaines »), cochés ✓ (évalués) ou ✗ (accès complet).
 * Abonné : tout coché, c'est le repère de ce que mesure le bilan ; compte gratuit : ✗ sur les domaines sans sujet
 * d'essai. `sombre` : variante pour la carte au fond foncé du parcours guidé.
 */
export function DomainesBilan({ domaines, sombre = false }) {
    const lignes = [...(domaines?.accessibles || []).map(d => ({ ...d, ok: true })), ...(domaines?.verrouilles || []).map(d => ({ ...d, ok: false }))];
    if (!lignes.length) return null;
    const nbOk = lignes.filter(d => d.ok).length;
    return (
        <div className={`guided-next__domaines${sombre ? ' guided-next__domaines--sombre' : ''}`}>
            <span className="guided-next__dom-titre">
                {nbOk < lignes.length
                    ? `Ton bilan gratuit couvre ${nbOk} domaines sur ${lignes.length}`
                    : `Ton bilan couvre les ${lignes.length} domaines du concours`}
            </span>
            <ul className="guided-next__dom-table" aria-label="Domaines évalués par le bilan">
                {lignes.map(d => (
                    <li key={d.id} className={`guided-next__dom ${d.ok ? 'guided-next__dom--ok' : 'guided-next__dom--ko'}`}>
                        {d.ok ? <Check size={14} aria-label="inclus" /> : <X size={14} aria-label="accès complet" />}
                        <span>{d.label}</span>
                    </li>
                ))}
            </ul>
            {nbOk < lignes.length && <span className="guided-next__dom-note"><X size={13} aria-label="Croix" /> : domaine évalué avec l’accès complet</span>}
        </div>
    );
}

// Visiteur non connecté : même carte, sans appel au plan (il n'y en a pas encore). Elle montre que la
// thématique commence par un bilan de connaissances ; le bouton mène à l'inscription (obligatoire).
export function GuidedNextStepTeaser({ free = false }) {
    const redirect = typeof window !== 'undefined' ? encodeURIComponent(window.location.pathname) : '';
    return (<>
        <div className="guided-next">
            <Illustration className="guided-next__illu" svg={illuGuide} />
            <div className="guided-next__copy">
                <span className="guided-next__eyebrow">Pour commencer</span>
                <strong className="guided-next__title">Fais ton bilan de connaissances</strong>
                <span className="guided-next__sub">
                    16 questions maximum pour repérer tes points d’appui et tes priorités, puis un parcours de révision guidé.
                    {free && <em> Accès complet gratuit en ce moment.</em>}
                </span>
            </div>
            <div className="guided-next__actions">
                <Link to={`/inscription${redirect ? `?redirect=${redirect}` : ''}`} className="btn btn-primary">
                    Créer mon compte gratuit <ArrowRight size={16} />
                </Link>
                <Link to="/connexion" className="guided-next__link">J’ai déjà un compte</Link>
            </div>
        </div>
        <span className="mode-dock__or">Ou découvre les modes</span>
    </>);
}

export default function GuidedNextStep({ scopeSlug, parcoursSlug = '', demo = false, onLaunch, onOpen, onChooseParcours = null, extra = null, onExpress = null }) {
    const [reco, setReco] = useState(null);
    const [parcours, setParcours] = useState(null); // { options, actif, requis }
    // Compte gratuit : domaines du bilan explorés par les sujets d'essai, et ceux réservés à l'accès complet.
    const [bilanDomaines, setBilanDomaines] = useState(null);
    const [state, setState] = useState('loading');
    // Une fois le choix du concours réaffiché après « Changer », on le ramène à l'écran.
    const choixRef = useRef(null);
    useEffect(() => {
        if (!retourAuChoixDemande || state !== 'ready' || !parcours?.requis || !choixRef.current) return;
        retourAuChoixDemande = false;
        choixRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [state, parcours]);

    useEffect(() => {
        if (!scopeSlug) return undefined;
        let cancelled = false;
        setState('loading');
        api.get('/user/learning-plan.php', { params: { scope: scopeSlug, parcours: parcoursSlug } })
            .then(({ data }) => { if (!cancelled) { setReco(data?.data?.recommendation || null); setParcours(data?.data?.parcours || null); setBilanDomaines(data?.data?.bilan_domaines || null); setState('ready'); } })
            .catch(() => { if (!cancelled) { setReco(null); setParcours(null); setState('error'); } });
        return () => { cancelled = true; };
    }, [scopeSlug, parcoursSlug]);

    // Rien d'exploitable : la ligne s'efface, les modes restent disponibles.
    if (state === 'error' || reco?.type === 'empty') return null;

    // Plusieurs concours, aucun choisi : un bilan « tous concours » n'a pas de sens.
    if (state === 'ready' && parcours?.requis && onChooseParcours) {
        return (<>
            <div className="guided-next" aria-live="polite" ref={choixRef}>
                <Illustration className="guided-next__illu" svg={illuGuide} />
                <div className="guided-next__copy">
                    <span className="guided-next__eyebrow">Avant ton bilan</span>
                    <strong className="guided-next__title">Tu prépares quel concours ?</strong>
                    <span className="guided-next__sub">Ton bilan et tes séances porteront uniquement sur ce concours. Tu pourras changer à tout moment.</span>
                </div>
                <div className="guided-next__choices" role="group" aria-label="Choisir le concours préparé">
                    {parcours.options.map(p => (
                        <button key={p.slug} type="button" className="btn btn-primary" onClick={() => onChooseParcours(p.slug)}>
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>
            <span className="mode-dock__or">Ou choisis toi-même</span>
        </>);
    }
    const parcoursOption = parcours?.actif && parcours.options?.length >= 2
        ? parcours.options.find(p => p.slug === parcours.actif) : null;
    const parcoursLabel = parcoursOption?.label || null;

    const t = TEXTS[reco?.type] || TEXTS.bilan;
    const title = typeof t.title === 'function' ? t.title(reco?.type === 'sm2' ? (reco.nb || 0) : reco) : t.title;
    // Révisions : le bouton annonce la taille réelle de la séance, et une séance express est proposée en plus.
    const dues = reco?.type === 'sm2' ? (reco.nb || 0) : 0;
    const seance = Math.min(SEANCE_SM2, dues);
    const cta = reco?.type === 'sm2' && seance > 0 ? `Démarrer ${seance} révision${seance > 1 ? 's' : ''}` : t.cta;
    const sub = reco?.type === 'sm2' && dues > SEANCE_SM2 ? `${t.sub} On commence par les ${SEANCE_SM2} plus urgentes.` : t.sub;
    const express = reco?.type === 'sm2' && dues > EXPRESS_SM2 && onExpress;
    const domainesBilan = state === 'ready' && ['bilan', 'rebilan', 'explore'].includes(reco?.type || 'bilan')
        && ((bilanDomaines?.accessibles?.length || 0) + (bilanDomaines?.verrouilles?.length || 0)) > 0;
    return (<>
        <div className="guided-next" aria-live="polite">
            <Illustration className="guided-next__illu" svg={illuGuide} />
            <div className="guided-next__copy">
                <span className="guided-next__eyebrow">Conseillé pour toi</span>
                {state === 'loading'
                    ? <strong className="guided-next__title guided-next__title--loading">Préparation de ta recommandation…</strong>
                    : <strong className="guided-next__title">{title}</strong>}
                <span className="guided-next__sub">
                    {state === 'loading' ? ' ' : sub}
                    {demo && state === 'ready' && !domainesBilan && <em> Gratuit sur les sujets de démonstration.</em>}
                </span>
                {domainesBilan && <DomainesBilan domaines={bilanDomaines} />}
                {parcoursLabel && (
                    <span className="guided-next__parcours">
                        Concours : <strong>{parcoursLabel}</strong>
                        {parcoursOption.badge && <span className="guided-next__badge">{parcoursOption.badge}</span>}
                        {onChooseParcours && <button type="button" className="guided-next__link" onClick={() => { retourAuChoixDemande = true; onChooseParcours(''); }}>Changer</button>}
                    </span>
                )}
            </div>
            <div className="guided-next__actions">
                <button type="button" className="btn btn-primary" onClick={onLaunch} disabled={state !== 'ready'}>
                    {cta} <ArrowRight size={16} />
                </button>
                {express && (
                    <button type="button" className="guided-next__link" onClick={() => onExpress(EXPRESS_SM2)}>
                        ou {EXPRESS_SM2} en express
                    </button>
                )}
                <button type="button" className="guided-next__link" onClick={onOpen}>Voir mon parcours guidé</button>
            </div>
        </div>
        {extra}
        <span className="mode-dock__or">Ou choisis toi-même</span>
    </>);
}
